import type { Layout } from './layout';
import { cellCenter } from './layout';
import type { EngineState } from '../types';
import { PI_2, clamp } from '../constants';

interface VisualParams {
  scale: number;
  alpha: number;
}

function paramsFromMotion(state: EngineState): VisualParams {
  // iOS:
  //   scale = (pitch + π/2) / π * 3.0
  //   alpha = (roll  + π/2) / π   (inverted if !invertPitch), then + 0.25
  const s = ((state.pitch + PI_2) / Math.PI) * 3.0;
  let a = (state.roll + PI_2) / Math.PI;
  if (!state.invertPitch) a = 1 - a;
  a += 0.25;
  return { scale: clamp(s, 0.2, 3.0), alpha: clamp(a, 0, 1) };
}

export function drawPlayhead(
  ctx: CanvasRenderingContext2D,
  layout: Layout,
  state: EngineState,
): void {
  const { x, y } = cellCenter(state.playBar, state.playVar, layout);
  const { scale, alpha } = paramsFromMotion(state);
  const r = layout.cellPx * 0.45 * scale;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = state.loop ? 'rgba(255,255,128,0.95)' : 'rgba(128,255,128,0.95)';
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export function drawTargetHead(
  ctx: CanvasRenderingContext2D,
  layout: Layout,
  state: EngineState,
): void {
  if (state.jumpBar === state.playBar && state.jumpVar === state.playVar) return;
  const { x, y } = cellCenter(state.jumpBar, state.jumpVar, layout);
  const r = layout.cellPx * 0.45;
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

export function drawConnector(
  ctx: CanvasRenderingContext2D,
  layout: Layout,
  state: EngineState,
): void {
  if (state.jumpBar === state.playBar && state.jumpVar === state.playVar) return;
  const a = cellCenter(state.playBar, state.playVar, layout);
  const b = cellCenter(state.jumpBar, state.jumpVar, layout);
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.restore();
}

export function drawLabels(
  ctx: CanvasRenderingContext2D,
  layout: Layout,
  state: EngineState,
): void {
  ctx.save();
  // Bright green "bar, variation" indicator, large and tracking the playhead vertically.
  // Matches the goldberg-variations-webapp styling: fill #00ff77, ~1.5× cell height,
  // right-aligned just outside the left edge of the grid.
  const fontPx = Math.max(14, layout.cellPx * 1.5);
  ctx.font = `bold ${fontPx}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  const labelGap = 8;
  const labelX = layout.originX - labelGap;
  // Variations are rows (y-axis), so the label tracks state.playVar vertically.
  const labelY = layout.originY + state.playVar * layout.cellPx + layout.cellPx / 2;
  ctx.fillStyle = '#00ff77';
  ctx.fillText(`${state.playBar + 1}, ${state.playVar + 1}`, labelX, labelY);

  // Target (pending-jump) indicator: ghost-green, on the RIGHT side at the target's row.
  if (state.jumpBar !== state.playBar || state.jumpVar !== state.playVar) {
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(0,255,119,0.45)';
    const targetX = layout.originX + layout.side + labelGap;
    const targetY = layout.originY + state.jumpVar * layout.cellPx + layout.cellPx / 2;
    ctx.fillText(`${state.jumpBar + 1}, ${state.jumpVar + 1}`, targetX, targetY);
  }
  ctx.restore();
}
